/**
 * domain/toolDrafts.js
 *
 * Gestión de borradores locales por herramienta rápida en VEYRA.
 * - Conserva prompt, referencias y configuración por separado para cada herramienta.
 * - Evita transferir parámetros incompatibles entre imagen, edición y vídeo.
 * - Aísla los borradores por usuario (userId) y proyecto (projectId || "global").
 * - Almacena estrictamente datos no sensibles (nunca claves, contraseñas ni binarios/blobs).
 */

const STORAGE_PREFIX = "veyra_tool_draft";

/**
 * Genera la clave de almacenamiento compuesta y aislada.
 */
export const getToolDraftKey = (userId, tool, projectId) => {
  const uid = userId || "anonymous";
  const prj = projectId || "global";
  const tId = tool || "unknown";
  return `${STORAGE_PREFIX}:${uid}:${prj}:${tId}`;
};

/**
 * Sanitiza y extrae únicamente los parámetros válidos y no sensibles de cada herramienta.
 */
export const sanitizeToolDraft = (tool, data) => {
  if (!data || typeof data !== "object") return null;

  const base = {
    prompt: typeof data.prompt === "string" ? data.prompt : "",
    modelId: typeof data.modelId === "string" ? data.modelId : "",
    connectionId: typeof data.connectionId === "string" ? data.connectionId : "",
  };

  switch (tool) {
    case "image":
      return {
        ...base,
        format: typeof data.format === "string" ? data.format : "auto",
        resolution: typeof data.resolution === "string" ? data.resolution : "",
        quantity: typeof data.quantity === "number" ? data.quantity : 1,
        refs: Array.isArray(data.refs) ? data.refs.filter((id) => typeof id === "string") : [],
        refAssetIds: Array.isArray(data.refAssetIds) ? data.refAssetIds.filter((id) => typeof id === "string") : [],
        characterRefs: Array.isArray(data.characterRefs)
          ? data.characterRefs.map((c) => ({
              characterId: c?.characterId || "",
              characterHandle: c?.characterHandle || "",
              assetId: c?.assetId || "",
              viewLabel: c?.viewLabel || "",
            }))
          : [],
      };

    case "edit":
      return {
        ...base,
        sourceId: typeof data.sourceId === "string" ? data.sourceId : "",
        format: typeof data.format === "string" ? data.format : "auto",
        resolution: typeof data.resolution === "string" ? data.resolution : "",
        refs: Array.isArray(data.refs) ? data.refs.filter((id) => typeof id === "string") : [],
        refAssetIds: Array.isArray(data.refAssetIds) ? data.refAssetIds.filter((id) => typeof id === "string") : [],
        characterRefs: Array.isArray(data.characterRefs)
          ? data.characterRefs.map((c) => ({
              characterId: c?.characterId || "",
              characterHandle: c?.characterHandle || "",
              assetId: c?.assetId || "",
              viewLabel: c?.viewLabel || "",
            }))
          : [],
      };

    case "video":
      return {
        ...base,
        startId: typeof data.startId === "string" ? data.startId : "",
        endId: typeof data.endId === "string" ? data.endId : "",
        motionVideoId: typeof data.motionVideoId === "string" ? data.motionVideoId : "",
        mode: typeof data.mode === "string" ? data.mode : "text_to_video",
        videoFormat: typeof data.videoFormat === "string" ? data.videoFormat : "16:9",
        videoResolution: typeof data.videoResolution === "string" ? data.videoResolution : "",
        duration: typeof data.duration === "number" ? data.duration : 5,
        audioEnabled: Boolean(data.audioEnabled),
        cinematicMode: typeof data.cinematicMode === "string" ? data.cinematicMode : "freeform",
        manualScenesEnabled: Boolean(data.manualScenesEnabled),
        shots: Array.isArray(data.shots)
          ? data.shots.map((s, idx) => ({
              index: typeof s?.index === "number" ? s.index : idx + 1,
              prompt: typeof s?.prompt === "string" ? s.prompt : "",
              duration: typeof s?.duration === "number" ? s.duration : 3,
            }))
          : [],
      };

    case "upscale":
      return {
        sourceId: typeof data.sourceId === "string" ? data.sourceId : "",
        upscaleModelId: typeof data.upscaleModelId === "string" ? data.upscaleModelId : "",
        upscaleScale: typeof data.upscaleScale === "number" ? data.upscaleScale : 2,
        upscaleFormat: typeof data.upscaleFormat === "string" ? data.upscaleFormat : "jpeg",
      };

    default:
      return base;
  }
};

/**
 * Recupera el borrador guardado para el usuario, herramienta y proyecto especificados.
 */
export const getToolDraft = (userId, tool, projectId) => {
  if (!tool) return null;
  try {
    const key = getToolDraftKey(userId, tool, projectId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return sanitizeToolDraft(tool, parsed);
  } catch {
    return null;
  }
};

/**
 * Guarda el borrador del usuario actual de manera segura y sin efectos colaterales.
 */
export const saveToolDraft = (userId, tool, projectId, draftData) => {
  if (!tool || !draftData || typeof draftData !== "object") return;
  try {
    const sanitized = sanitizeToolDraft(tool, draftData);
    if (!sanitized) return;
    const key = getToolDraftKey(userId, tool, projectId);
    localStorage.setItem(key, JSON.stringify(sanitized));
  } catch {
    // Manejar cuota excedida o almacenamiento no disponible
  }
};

/**
 * Elimina el borrador específico de una herramienta.
 */
export const clearToolDraft = (userId, tool, projectId) => {
  if (!tool) return;
  try {
    const key = getToolDraftKey(userId, tool, projectId);
    localStorage.removeItem(key);
  } catch {}
};
